/**
 * Arm/Disarm switch with ConfirmDialog for the arming step.
 * Disarm = immediate (no confirm). Arm = explicit ConfirmDialog naming consequence.
 * Safety rule: NEVER auto-arm. This component is the single go-live gate.
 */
import { useState, type ReactNode } from 'react';
import { Switch } from '@vnetwork/ui/primitives';
import { ConfirmDialog } from '@vnetwork/ui/feedback';
import type { Playbook } from '@/api/healthcheck';

interface Props {
  playbook: Playbook;
  onArm: () => void;
  onDisarm: () => void;
  isPending?: boolean;
  /** Count of alarms currently attached to this playbook. */
  attachedAlarmCount?: number;
}

export function ArmToggle({ playbook, onArm, onDisarm, isPending, attachedAlarmCount = 0 }: Props) {
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleChange = (checked: boolean) => {
    if (checked) {
      // Arming: require explicit confirm
      setConfirmOpen(true);
    } else {
      // Disarming: safe, no confirm needed
      onDisarm();
    }
  };

  const armDescription = buildArmDescription(playbook, attachedAlarmCount);

  return (
    <>
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <Switch
          checked={playbook.is_active}
          onCheckedChange={handleChange}
          disabled={isPending}
          aria-label={playbook.is_active ? 'Disarm playbook' : 'Arm playbook'}
        />
        <span
          style={{
            fontSize: 'var(--vn-font-size-sm)',
            fontWeight: 'var(--vn-font-weight-medium)' as React.CSSProperties['fontWeight'],
            color: playbook.is_active ? 'var(--vn-success-text)' : 'var(--vn-fg-muted)',
          }}
        >
          {playbook.is_active ? 'Armed' : 'Disarmed'}
        </span>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={(o) => !o && setConfirmOpen(false)}
        destructive
        title={`Arm "${playbook.name}"?`}
        description={armDescription}
        confirmLabel="Arm playbook"
        onConfirm={() => {
          setConfirmOpen(false);
          onArm();
        }}
      />
    </>
  );
}

function buildArmDescription(playbook: Playbook, alarmCount: number): ReactNode {
  const actions = playbook.actions ?? [];
  const hasDestructive = actions.some(
    (a) => a.type === 'swift_blacklist' || a.type === 'alibaba_blacklist',
  );

  const lines: string[] = [
    'Arming lets this playbook AUTOMATICALLY execute its actions whenever a linked alarm fires.',
  ];

  if (hasDestructive) {
    lines.push(
      'This playbook contains edge-blocking actions that affect LIVE traffic — real users matching the alarm condition will be blocked.',
    );
    lines.push(
      'Edge blocks are effectively irreversible until the configured block duration ends.',
    );
  }

  if (alarmCount === 0) {
    lines.push(
      'This playbook is not attached to any alarm yet, so arming has no immediate effect — nothing will trigger until you attach an alarm.',
    );
  } else {
    lines.push(`${alarmCount} attached alarm${alarmCount === 1 ? '' : 's'} can trigger this playbook.`);
  }

  return (
    <ul style={{ margin: 0, paddingLeft: 'var(--vn-space-4)', display: 'grid', gap: 'var(--vn-space-1)' }}>
      {lines.map((l, i) => <li key={i}>{l}</li>)}
    </ul>
  );
}
